import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { X, Check, Users, Truck, UserCheck } from 'lucide-react';

interface ProfileWithRole {
  id: string;
  name: string;
  role: string;
}

interface CreateGroupDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}

const CreateGroupDialog = ({ open, onClose, onCreated }: CreateGroupDialogProps) => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [profiles, setProfiles] = useState<ProfileWithRole[]>([]);
  const [groupName, setGroupName] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [creating, setCreating] = useState(false);
  const [activeFilter, setActiveFilter] = useState<'all' | 'employee' | 'driver' | 'customer'>('all');
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!open || !user) return;
    const fetchProfiles = async () => {
      const { data: profilesData } = await supabase.from('profiles_public' as any).select('id, name') as any;
      const { data: rolesData } = await supabase.from('user_roles').select('user_id, role');

      const roleMap: Record<string, string> = {};
      (rolesData || []).forEach(r => { roleMap[r.user_id] = r.role; });

      const enriched: ProfileWithRole[] = (profilesData || [])
        .filter(p => p.id !== user.id)
        .map(p => ({
          ...p,
          role: roleMap[p.id] || 'employee',
        }));

      setProfiles(enriched);
    };
    fetchProfiles();
  }, [open, user]);

  const filtered = profiles
    .filter(p => activeFilter === 'all' || p.role === activeFilter)
    .filter(p => p.name.toLowerCase().includes(search.toLowerCase()));

  const toggleMember = (id: string) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleCreate = async () => {
    if (!user || !groupName.trim() || selectedIds.length === 0 || creating) return;
    setCreating(true);

    const { data: newRoom, error: roomError } = await supabase
      .from('chat_rooms')
      .insert({ type: 'group' as any, name: groupName.trim(), created_by: user.id })
      .select()
      .single();

    console.log('Create room result:', { newRoom, roomError });

    if (roomError) {
      console.error('Failed to create room:', roomError);
      setCreating(false);
      return;
    }

    if (newRoom) {
      const members = [user.id, ...selectedIds].map(uid => ({
        room_id: newRoom.id,
        user_id: uid,
      }));
      const { error: memberError } = await supabase.from('chat_room_members').insert(members as any);
      console.log('Add members result:', { memberError });
    }

    setCreating(false);
    setGroupName('');
    setSelectedIds([]);
    setSearch('');
    setActiveFilter('all');
    onClose();
    onCreated();
  };

  const getAvatarColor = (name: string) => {
    const colors = ['#25D366', '#128C7E', '#075E54', '#34B7F1', '#00A884'];
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return colors[Math.abs(hash) % colors.length];
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'driver': return <Truck className="h-3 w-3" />;
      case 'customer': return <UserCheck className="h-3 w-3" />;
      default: return <Users className="h-3 w-3" />;
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'driver': return t('driver');
      case 'customer': return t('customer');
      case 'admin': return t('role') + ' Admin';
      default: return t('userManagement').split(' ')[0] || 'موظف';
    }
  };

  const filters = [
    { key: 'all' as const, label: t('all') },
    { key: 'employee' as const, label: t('userManagement').split(' ')[0] || 'موظفين' },
    { key: 'driver' as const, label: t('driver') },
    { key: 'customer' as const, label: t('customer') },
  ];

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { onClose(); setGroupName(''); setSelectedIds([]); setSearch(''); setActiveFilter('all'); } }}>
      <DialogContent className="sm:max-w-md p-0 overflow-hidden">
        <DialogHeader className="bg-[#075E54] text-white px-4 py-3">
          <DialogTitle className="text-white">{t('newGroup')}</DialogTitle>
        </DialogHeader>

        <div className="p-4 space-y-3">
          <Input
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            placeholder={t('groupName')}
            className="border-0 border-b-2 border-[#25D366] rounded-none focus-visible:ring-0 text-[#111b21]"
          />

          {/* Selected members chips */}
          {selectedIds.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {selectedIds.map(id => {
                const p = profiles.find(x => x.id === id);
                if (!p) return null;
                return (
                  <span key={id} className="inline-flex items-center gap-1 bg-[#e7fce3] text-[#075E54] rounded-full px-3 py-1 text-sm">
                    {getRoleIcon(p.role)}
                    {p.name}
                    <X className="h-3.5 w-3.5 cursor-pointer" onClick={() => toggleMember(id)} />
                  </span>
                );
              })}
            </div>
          )}

          {/* Role filter tabs */}
          <div className="flex gap-1 bg-[#f0f0f0] rounded-lg p-1">
            {filters.map(f => (
              <button
                key={f.key}
                onClick={() => setActiveFilter(f.key)}
                className={`flex-1 text-xs py-1.5 px-2 rounded-md transition-colors ${
                  activeFilter === f.key
                    ? 'bg-[#075E54] text-white'
                    : 'text-[#667781] hover:bg-white'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>

          {/* Search */}
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('searchContacts')}
            className="rounded-full bg-[#f0f0f0] border-0 text-sm h-9"
          />
        </div>

        <div className="max-h-52 overflow-y-auto">
          {filtered.map((profile) => {
            const selected = selectedIds.includes(profile.id);
            return (
              <div
                key={profile.id}
                className="flex items-center gap-3 px-4 py-2.5 hover:bg-[#f0f0f0] cursor-pointer"
                onClick={() => toggleMember(profile.id)}
              >
                <Avatar className="h-10 w-10">
                  <AvatarFallback style={{ backgroundColor: getAvatarColor(profile.name) }} className="text-white font-medium text-sm">
                    {profile.name.charAt(0).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <span className="text-[#111b21] text-sm block">{profile.name}</span>
                  <span className="text-[10px] text-[#667781] flex items-center gap-1">
                    {getRoleIcon(profile.role)}
                    {getRoleLabel(profile.role)}
                  </span>
                </div>
                {selected && (
                  <div className="w-6 h-6 rounded-full bg-[#25D366] flex items-center justify-center shrink-0">
                    <Check className="h-4 w-4 text-white" />
                  </div>
                )}
              </div>
            );
          })}
          {filtered.length === 0 && (
            <p className="text-center text-[#667781] py-6 text-sm">{t('noUsers')}</p>
          )}
        </div>

        <div className="p-3 border-t flex justify-center">
          <button
            onClick={handleCreate}
            disabled={!groupName.trim() || selectedIds.length === 0 || creating}
            className="w-12 h-12 rounded-full bg-[#25D366] text-white flex items-center justify-center hover:bg-[#128C7E] transition-colors disabled:opacity-50"
          >
            <Check className="h-6 w-6" />
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CreateGroupDialog;
